import { ReactElement, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import BigNumber from 'bignumber.js';
import { format } from 'date-fns';

import { DEFAULT_SHORT_DECIMAL_PLACES } from 'modules/common';

interface IChartPoint {
  date: Date;
  timestamp: number;
  value: number | null;
  displayValue?: number | null;
  isEdge: boolean;
}

interface IExtendedChartPoint extends IChartPoint {
  payload: IChartPoint;
}

type Props = {
  active?: boolean;
  payload?: IExtendedChartPoint[];
  label?: number;
  decimals?: number;
  prefix?: string;
  postfix?: string;
};

export function ChartTooltipContent({
  active,
  payload,
  label,
  prefix = '',
  postfix = '',
  decimals = DEFAULT_SHORT_DECIMAL_PLACES,
}: Props): ReactElement | null {
  const value = payload?.[0]?.value;

  const formatValue = useCallback(
    (amount: number | null | undefined) =>
      amount
        ? new BigNumber(amount).toFormat(decimals, BigNumber.ROUND_DOWN, {
            groupSeparator: ',',
            groupSize: 3,
            decimalSeparator: '.',
          })
        : '',
    [decimals],
  );

  if (!active || !payload || !payload.length || typeof value !== 'number') {
    return null;
  }

  const { payload: extendedPayload } = payload[0];
  const extendedValue = extendedPayload?.value;
  const displayValue = extendedPayload?.displayValue;

  if (typeof extendedValue === 'number' && extendedValue !== value) {
    return null;
  }

  const isEdge = extendedPayload?.isEdge || false;
  if (isEdge) {
    return null;
  }

  const dateStr = format(new Date(label || 0), 'd MMM ha');

  return (
    <Box
      sx={theme => ({
        background: theme.palette.common.white,
        borderRadius: '8px',
        minWidth: 92,
        minHeight: 36,
        px: 2,
        py: 1,
        padding: theme.spacing(1, 2),
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'none',
      })}
    >
      <Typography color="background.default" variant="body2">
        {dateStr}
      </Typography>

      <Typography color="background.default" fontWeight={600} variant="body2">
        {`${prefix}${formatValue(displayValue || value)} ${postfix}`}
      </Typography>
    </Box>
  );
}
